import "./ResetPassword.css";

import { faCheck, faExclamation } from "@fortawesome/free-solid-svg-icons";
import { forgotPassword, resetCheck, resetPassword } from "../../api/auth";
import { setForgotPasswordVisible, setLoginVisible } from "../../store/modals/modalActions";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import CustomInput from "../../components/CustomInput/CustomInput";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Footer from "../../components/Footer/Footer";
import Header from "../../components/Header/Header";
import PasswordValidator from "../../components/PasswordValidator/PasswordValidator";
import ReactLoading from "react-loading";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../hooks/reduxHooks";

const ResetPassword = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const token = searchParams.get("token");
  const email = searchParams.get("email");
  const [password, setPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [success, setSuccess] = useState<boolean>(false);
  const [errors, setErrors] = useState<string[]>([]);

  const [resetResponse, setResetResponse] = useState<any | null>(null);
  const [retrySent, setRetrySent] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    resetCheck(token!, email!).then((resp) => {
      setResetResponse(resp);
    });
    if (!token) {
      navigate("/");
    }
    dispatch(setLoginVisible(false));
    dispatch(setForgotPasswordVisible(false));
  }, []);

  const validate = () => {
    if (typeof password === "undefined" || password?.length === 0) {
      setErrors((errors) => [...errors, "Please enter a new password"]);
      return false;
    }
    if (password?.trim() !== confirmPassword?.trim()) {
      setErrors((errors) => [...errors, "Passwords must match"]);
      return false;
    }
    if (password && password?.trim().length < 8) {
      setErrors((errors) => [...errors, "Password must be 8 characters or more"]);
      return false;
    }
    if (!/[A-Z]/.test(password!)) {
      setErrors((errors) => [...errors, "Password must contain at least one uppercase character"]);
      return false;
    }

    if (!/[a-z]/.test(password!)) {
      setErrors((errors) => [...errors, "Password must contain at least one lowercase character"]);
      return false;
    }

    if (!/[ `!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?~]/.test(password!)) {
      setErrors((errors) => [...errors, "Password must include at least one special character"]);
      return false;
    }
    if (!/\d/.test(password!)) {
      setErrors((errors) => [...errors, "Password must include at least one number"]);
      return false;
    }

    if (!password) {
      document.getElementById("password-input")!.style.border = "1px solid red";
      setErrors((errors) => [...errors, "Please enter a password"]);
      return false;
    } else {
      document.getElementById("password-input")!.style.border = "1px solid black";
    }
    if (!confirmPassword) {
      document.getElementById("confirm-password-input")!.style.border = "1px solid red";
      setErrors((errors) => [...errors, "Please confirm your password"]);
      return false;
    } else {
      document.getElementById("confirm-password-input")!.style.border = "1px solid black";
    }
    return true;
  };

  const onResetPasswordClicked = () => {
    setErrors([]);
    if (validate()) {
      setErrors([]);
      resetPassword(password!, confirmPassword!, token!, email!).then((resp: any) => {
        setSuccess(resp.status);
        if (!resp.status) {
          toast.error(resp.message ? resp.message : "Error updating password", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      });
    }
  };

  const onLoginClicked = () => {
    dispatch(setLoginVisible(true));
  };

  const onRetryClicked = () => {
    if (resetResponse.email) {
      setLoading(true);
      forgotPassword(resetResponse.email)
        .then((response) => {
          if (response.status) {
            setRetrySent(response.status);
          }
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <div className="reset-password-container">
      <Header title="City of Cape Town - Traffic Fines" hideLoginButton />
      <div className="reset-password-content">
        {!success && resetResponse && resetResponse.status === "success" ? (
          <>
            <p className="reset-password-heading">Reset your password</p>
            <div className="reset-form">
              <p className="reset-form-heading">Please enter your new password</p>
              <div className="reset-form-container">
                <CustomInput
                  label="PASSWORD"
                  id="password-input"
                  type="password"
                  value={password}
                  setValue={setPassword}
                  placeholder="Password"
                  containerStyle={{ width: "48vw" }}
                />
                <CustomInput
                  label="CONFIRM PASSWORD"
                  id="confirm-password-input"
                  type="password"
                  value={confirmPassword}
                  setValue={setConfirmPassword}
                  placeholder="Confirm Password"
                  containerStyle={{ width: "48vw" }}
                />
                {errors && errors.length > 0 ? (
                  <div className="reset-error-container">
                    <p className="error-text">{errors[0]}</p>
                  </div>
                ) : null}
              </div>
              <div style={{ width: "60%" }}>
                <PasswordValidator password={password} newPassword={confirmPassword} />
              </div>

              <div className="reset-password-button" onClick={() => onResetPasswordClicked()}>
                <p>Reset Password</p>
              </div>
            </div>
          </>
        ) : success && resetResponse && resetResponse.status === "success" ? (
          <div className="reset-success">
            <div className="reset-success-header">
              <FontAwesomeIcon className="payment-icon" icon={faCheck} size="3x" color="#18283c" />

              <p>Your password has been reset</p>
            </div>
            <div className="reset-success-login-button" onClick={() => onLoginClicked()}>
              <p>Login</p>
            </div>
          </div>
        ) : resetResponse ? (
          <div className="reset-success">
            <div className="reset-success-header">
              <FontAwesomeIcon
                className="payment-icon"
                icon={faExclamation}
                size="3x"
                color="#18283c"
              />
              <p>{resetResponse?.message}</p>
            </div>
            {!retrySent ? (
              resetResponse.email ? (
                <div className="reset-success-login-button" onClick={() => onRetryClicked()}>
                  {!loading ? <p>Resend Link</p> : <ReactLoading type="bubbles" color="white" />}
                </div>
              ) : (
                <p>Please try again</p>
              )
            ) : (
              <div className="reset-success-login-button">
                <p>Email Sent</p>
              </div>
            )}
          </div>
        ) : (
          <ReactLoading type="bubbles" color="#e97700" height={"50px"} width={"50px"} />
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
